import { Asset } from '@/api/generatedApi';
import { useMatchLink } from '@/hooks/useMatchLink';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { MatchIcon } from '../AssetsPage/MatchIcon';

const getTooltipTitle = ({
  linkTo,
  isDisabled,
  disabledReason,
}: {
  linkTo?: string;
  isDisabled: boolean;
  disabledReason: string;
}) => {
  if (!linkTo) {
    return disabledReason;
  }

  if (isDisabled) {
    return;
  }

  return 'Find Matches';
};

export const FindMatchesButton = ({
  asset,
  variant,
  minThreshold,
  showDisabled = false,
  disabledReason = 'Find matches unavailable',
}: {
  asset: Asset;
  variant?: 'button' | 'icon';
  minThreshold?: number;
  showDisabled?: boolean;
  disabledReason?: string;
}) => {
  const linkTo = useMatchLink(asset, minThreshold);
  const { assetId: searchAssetId } = useSearchFilters();
  const isDisabled = asset.id === searchAssetId || !linkTo;

  if (!linkTo && !showDisabled) {
    return;
  }

  const tooltipTitle = getTooltipTitle({ linkTo, isDisabled, disabledReason });
  const testId = AssetPageObjectModel.FindMatchesButton;

  const buttonElement =
    variant === 'icon' ? (
      <IconButton size='small' disabled={isDisabled} data-testid={isDisabled ? testId : undefined}>
        <MatchIcon />
      </IconButton>
    ) : (
      <Button
        variant='text'
        size='small'
        color='primary'
        startIcon={<MatchIcon />}
        disabled={isDisabled}
        data-testid={isDisabled ? testId : undefined}
      >
        Find Matches
      </Button>
    );

  const wrappedButton =
    isDisabled || !linkTo ? (
      buttonElement
    ) : (
      <Link to={linkTo} data-testid={testId}>
        {buttonElement}
      </Link>
    );

  if (variant === 'icon') {
    return (
      <Tooltip title={tooltipTitle}>
        <span>{wrappedButton}</span>
      </Tooltip>
    );
  }

  return wrappedButton;
};
